import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Loading } from "@/components/atoms";
import { GeneralFooter, GeneralHeader } from "@/components/organisms";
import { KYCVerificationStep, PlatformSetupStep, PlanSelectionStep } from "@/components/organisms/Onboarding";
import { useLazyGetAccountsQuery } from "@/redux/apis/account/accountApi";
import { useLazyVerifyProviderQuery } from "@/redux/apis/auth/authApi";
import { useLazyGetOperatorsQuery } from "@/redux/apis/operator/operatorApi";
import { useLazyGetProfileQuery } from "@/redux/apis/profile/profileApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setActiveAccount } from "@/redux/slices/account/accountSlice";
import { siteInfoSelector } from "@/redux/slices/config/selectors";
import { setActiveOperator } from "@/redux/slices/operator/operatorSlice";

type OnboardingStep = "kyc" | "platform-setup" | "plan-selection" | "complete";

// Type definitions for Kinde claims
type UserProperty = { v: string };
type ClaimsWithUserProperties = {
  user_properties?: {
    kp_usr_utm_content?: UserProperty;
    kp_usr_utm_medium?: UserProperty;
    kp_usr_utm_campaign?: UserProperty;
  };
};

/**
 * OperatorOnboarding - Three-step onboarding for operators
 *
 * Steps:
 * 1. KYC Verification (Sumsub) - automatically resumes if already started
 * 2. Platform Setup (Business details) - skipped if operator already exists
 * 3. Plan Selection (Subscription) - final step to complete registration
 *
 * Smart step detection:
 * - KYC step: Sumsub SDK automatically detects and resumes
 * - Platform Setup: Checks if operators exist, skips to plan selection if yes
 * - Plan Selection: Final step that marks registration as complete
 *
 * After completion, redirects to auth-callback for authentication
 */
export const OperatorOnboarding = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const siteInfo = useAppSelector(siteInfoSelector);
  const { getClaims } = useKindeAuth();
  const [getAccounts] = useLazyGetAccountsQuery();
  const [getProfile] = useLazyGetProfileQuery();
  const [getOperators] = useLazyGetOperatorsQuery();
  const [verifyProvider] = useLazyVerifyProviderQuery();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>("kyc");
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(true);

  // Check if user has completed onboarding by checking profile metadata
  useEffect(() => {
    // First check user type via Kinde claims
    getClaims("idToken")
      .then((claims) => {
        const typedClaims = claims as ClaimsWithUserProperties;
        const utmContent = typedClaims?.user_properties?.kp_usr_utm_content?.v;

        // If client is trying to access operator onboarding, redirect to auth-callback
        if (utmContent === "client") {
          navigate("/auth-callback", { replace: true });
          return Promise.reject(new Error("Client redirected to auth-callback"));
        }

        // Continue with operator flow - fetch and set the active account, then check profile
        return getAccounts().unwrap();
      })
      .then((res) => {
        const accounts = res.data.accounts;
        const primaryAccount = accounts.find((account) => account.primary);

        if (!primaryAccount) {
          console.error("No primary account found");
          setIsCheckingOnboarding(false);
          return;
        }

        // Set active account in Redux so profile API can use it
        dispatch(setActiveAccount(primaryAccount));

        // Now fetch profile to check registration status
        return getProfile().unwrap();
      })
      .then((profile) => {
        if (profile?.accountMetadata?.isRegisterComplete) {
          // Operator has completed registration, redirect to auth-callback for re-authentication
          navigate("/auth-callback", { replace: true });
        } else {
          setIsCheckingOnboarding(false);
        }
      })
      .catch((error) => {
        // Account or profile fetch failed, proceed with onboarding
        // (unless it's a client redirect, which is expected)
        if (error.message !== "Client redirected to auth-callback") {
          setIsCheckingOnboarding(false);
        }
      });
  }, [getAccounts, getProfile, dispatch, navigate, getClaims]);

  useEffect(() => {
    // Fetch UTM parameters from Kinde claims and set active operator
    getClaims("idToken")
      .then((claims) => {
        const typedClaims = claims as ClaimsWithUserProperties;
        const utmCampaign = typedClaims?.user_properties?.kp_usr_utm_campaign?.v;

        // Set active operator from UTM campaign to prevent issues with cached operators
        if (utmCampaign && siteInfo?.apiUrl) {
          verifyProvider({
            providerCode: utmCampaign,
            apiUrl: siteInfo.apiUrl,
          })
            .unwrap()
            .then((operator) => {
              dispatch(setActiveOperator(operator));
            })
            .catch((error) => {
              console.error("Error setting active operator from claims:", error);
            });
        }
      })
      .catch((error) => {
        console.error("Error fetching Kinde claims:", error);
      });
  }, [getClaims, verifyProvider, dispatch, siteInfo]);

  const handleKYCComplete = async () => {
    // After KYC, check if operator already exists
    try {
      const operatorsResponse = await getOperators().unwrap();
      const operators = operatorsResponse.data.operators;

      if (operators && operators.length > 0) {
        // Operator exists, set as active operator and skip to plan selection
        const primaryOperator = operators[0]; // Get the first operator (user's operator)
        dispatch(setActiveOperator(primaryOperator));
        setCurrentStep("plan-selection");
      } else {
        // No operator, proceed to platform setup
        setCurrentStep("platform-setup");
      }
    } catch (error) {
      console.error("Error checking operators:", error);
      // On error, default to platform setup
      setCurrentStep("platform-setup");
    }
  };

  const handlePlatformSetupComplete = () => {
    // Platform setup done, proceed to plan selection
    setCurrentStep("plan-selection");
  };

  const handlePlanSelectionComplete = () => {
    setCurrentStep("complete");
    // Registration complete → redirect to auth-callback
    navigate("/auth-callback", { replace: true });
  };

  // Show loading while checking if user has completed onboarding
  if (isCheckingOnboarding) {
    return <Loading />;
  }

  return (
    <div className="flex min-h-screen flex-row overflow-hidden">
      <div className="relative flex flex-1 flex-col">
        <GeneralHeader action="logout" />
        <Container className="relative flex w-full max-w-[1040px] flex-1 items-start justify-center gap-5 px-5 py-8">
          <div className="flex w-full justify-center">
            {currentStep === "kyc" && <KYCVerificationStep onVerificationComplete={handleKYCComplete} />}
            {currentStep === "platform-setup" && <PlatformSetupStep onSetupComplete={handlePlatformSetupComplete} />}
            {currentStep === "plan-selection" && <PlanSelectionStep onPlanSelected={handlePlanSelectionComplete} />}
          </div>
        </Container>
        <GeneralFooter />
      </div>
    </div>
  );
};
